import { createContext, useContext, useMemo, type ReactNode } from "react";
import { LEVEL_ADMIN, LEVEL_CAMERA_VIEWER, LEVEL_TEAM_MEMBER } from "./client";

/**
 * What the signed-in user is allowed to do, derived once from the access level
 * that /api/auth/me reports.
 *
 * This hides controls; it does not enforce anything. Every one of these actions
 * is re-checked server-side and answers 403 regardless of what the UI shows —
 * see AccessGuard on the API. The point here is not showing people buttons that
 * would only fail.
 */
export interface Access {
  level: number;
  /** Level 2+. The override is shared data the whole team reads. */
  canOverrideSpecies: boolean;
  /** Level 2+. Hiding removes a frame from everyone's view. */
  canHide: boolean;
  /** Level 2+. The Schedule and its Events. */
  canUseSchedule: boolean;
  /** Level 3. Audit trail and user management. */
  isAdmin: boolean;
}

const AccessContext = createContext<Access>({
  level: LEVEL_CAMERA_VIEWER,
  canOverrideSpecies: false,
  canHide: false,
  canUseSchedule: false,
  isAdmin: false,
});

export function AccessProvider({ level, children }: { level: number; children: ReactNode }) {
  const value = useMemo<Access>(
    () => ({
      level,
      canOverrideSpecies: level >= LEVEL_TEAM_MEMBER,
      canHide: level >= LEVEL_TEAM_MEMBER,
      canUseSchedule: level >= LEVEL_TEAM_MEMBER,
      isAdmin: level >= LEVEL_ADMIN,
    }),
    [level],
  );
  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useAccess(): Access {
  return useContext(AccessContext);
}
